import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";

import { MANAGER_CALL_ACTIONS } from "@regal-voice/shared-types";
import { Device, Call } from "@twilio/voice-sdk";
import noop from "lodash/noop";
import omit from "lodash/omit";
import { useSelector } from "react-redux";

import { hasMicPerms$ } from "App/MicPermissionsCheck";
import { useRefWithEffect } from "Hooks/useRefWithEffect/useRefWithEffect";
import { useWrapTask } from "Hooks/useWrapTask/useWrapTask";
import { authenticateConversationsUser } from "Services/ConversationsApiService";
import { useFlags } from "Services/FeatureFlagService";
import { addLoggerGlobalContext, getLogger, renderErrorMessage } from "Services/LoggingService";
import {
    selectAgentPreferredEdge,
    selectOnActiveCall,
    setOnActiveCall,
} from "Services/state/agent/AgentInformationSlice";
import { clearConference, setDeviceParams } from "Services/state/conferences/ConferenceStateSlice";
import { updateConnectionQualityStatistics } from "Services/state/conferences/ConnectionQualityStateSlice";
import {
    warn as networkQualityWarning,
    resolveWarning as resolveNetworkQualityWarning,
    clearWarnings as clearNetworkQualityWarnings,
} from "Services/state/conferences/NetworkQualitySlice";
import { selectActiveConferenceTask } from "Services/state/conferences/Selectors";
import { selectActiveAction } from "Services/state/manager-action/Selectors";
import { clearManagerAction } from "Services/state/manager-action/Thunks";
import { useRVDispatch } from "Services/state/Storage";
import { ConnectionStats } from "Types/ConnectionStats";
import {
    TwilioError,
    twillioVoiceErrorCodeMappings,
    twilioTokenErrorCodes,
    EXPIRED_JWT,
    INVALID_JWT,
    twilioNoisyErrorsToIgnore,
} from "Types/TwilioError";

import { logger, deviceLogger, type TwilioLogLevel } from "./deviceLogger";

// @twilio/voice-sdk doesn't expose OutputDeviceCollection through its package "exports",
// and we only use .get() here, so we type the minimal shape instead of importing the internal module.
type TwilioOutputDevices = { get(): Set<MediaDeviceInfo> };

const errorMessageCache: Record<string, number> = {};
const callStateLogger = getLogger("Twilio Connection Warnings");
const audioStatisticsLogger = getLogger("Audio statistics");

export const DEVICE_DEFAULT_OPTIONS: Device.Options = {
    enableImprovedSignalingErrorPrecision: true,
    codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
};

export const __DEFAULT_DEVICE_EDGES = ["roaming", "ashburn"];

type TwilioDeviceState = {
    device?: Device;
    activeCall?: Call;
    setActiveCall: (call?: Call) => void;
    fetchNewAuthToken: () => Promise<void>;
};

export const TwilioDeviceContext = createContext<TwilioDeviceState>({
    device: undefined,
    activeCall: undefined,
    setActiveCall: noop,
    fetchNewAuthToken: () => Promise.resolve(),
});

export function TwilioDeviceProvider({ children }: PropsWithChildren): JSX.Element {
    const [activeCall, setActiveCall] = useState<Call | undefined>();
    const [device, setDevice] = useState<Device | undefined>();
    const [token, setToken] = useState("");
    const preferredEdge = useSelector(selectAgentPreferredEdge);
    const { voiceSdkLogLevel, voiceSdkDdLogger, voiceSdkCloseProtection } = useFlags();
    const logLevel = voiceSdkLogLevel as TwilioLogLevel;

    const fetchNewAuthToken = useCallback(async () => {
        const { token } = await authenticateConversationsUser();
        setToken(token);
    }, []);

    useEffect(() => {
        fetchNewAuthToken();
    }, [fetchNewAuthToken]);

    useEffect(() => {
        if (!token) {
            setDevice(undefined);
            return;
        }

        if (device) {
            if (token !== device.token) {
                logger.log("Updating device token");
                device.updateToken(token);
                if (![Device.State.Registered, Device.State.Registering].includes(device.state)) {
                    // if for some reason the device went into a bad state with the previous token, we need to re-register after setting the new one
                    device.register().catch((error) => {
                        renderErrorMessage({
                            content:
                                "An error occurred while registering for phone calls. Please refresh the page and try again.",
                            error: error instanceof Error ? error : new Error(error ? String(error) : "Unknown error"),
                            logger,
                            logAsWarn: true,
                            extraData: { error },
                        });
                    });
                }
            }
            if (preferredEdge && preferredEdge !== device.edge) {
                device.updateOptions({ edge: [preferredEdge, ...__DEFAULT_DEVICE_EDGES] });
            }
            return;
        }

        const newDevice = new Device(token, {
            ...DEVICE_DEFAULT_OPTIONS,
            logLevel,
            closeProtection: voiceSdkCloseProtection,
            ...(preferredEdge ? { edge: [preferredEdge, ...__DEFAULT_DEVICE_EDGES] } : {}),
        });

        deviceLogger.setDefaultLevel(logLevel);
        if (voiceSdkDdLogger) {
            newDevice["_log"] = deviceLogger;
        }

        // Shouldn't we only set this if register succeeds? in a .then?
        // also device listeners should be added here before we call resgister
        // or there are race conditions with logging
        setDevice(newDevice);
        newDevice.register().catch((error) => {
            renderErrorMessage({
                content: "An error occurred while registering for phone calls. Please refresh the page and try again.",
                error: error instanceof Error ? error : new Error(String(error)),
                logger,
                extraData: { error },
            });
        });
    }, [token, preferredEdge, device, logLevel, voiceSdkDdLogger, voiceSdkCloseProtection]);

    useEffect(() => {
        if (!device) {
            return;
        }

        device.updateOptions({ logLevel });
        if (voiceSdkDdLogger) {
            device["_log"] = deviceLogger;
        }
    }, [device, logLevel, voiceSdkDdLogger]);

    useEffect(() => {
        if (!device) {
            return;
        }

        device.updateOptions({ closeProtection: voiceSdkCloseProtection });

        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to watch the flag.
    }, [voiceSdkCloseProtection]);

    return (
        <TwilioDeviceContext.Provider value={{ device, activeCall, setActiveCall, fetchNewAuthToken }}>
            {children}
        </TwilioDeviceContext.Provider>
    );
}

export const useTwilioDevice = (): TwilioDeviceState => useContext(TwilioDeviceContext);

/**
 * This hook sets up the needed Twilio device listeners. IT IS **NOT REUSABLE**. DO NOT INVOKE MORE THAN ONCE IN THE WHOLE APP.
 * It returns some of its handlers just for testing purposes.
 */
export function useTwilioDeviceListeners(): {
    handleIncomingCall: (call: Call) => void;
    handleCallConnected: (connection: Call) => void;
    handleCallDisconnected: (connection: Call) => void;
} {
    const [connStats, setConnStats] = useState<Array<ConnectionStats>>([]);
    const [totalConnStats, setTotalConnStats] = useState<ConnectionStats["totals"]>(
        {} as unknown as ConnectionStats["totals"]
    );
    const { device, setActiveCall, fetchNewAuthToken } = useContext(TwilioDeviceContext);
    const wrapTask = useWrapTask();

    const dispatch = useRVDispatch();
    const onActiveCall = useSelector(selectOnActiveCall);

    const managerAction = useSelector(selectActiveAction);
    const managerActionRef = useRefWithEffect(managerAction);

    const activeConferenceTask = useSelector(selectActiveConferenceTask);
    const activeConferenceTaskRef = useRefWithEffect(activeConferenceTask);

    useEffect(() => {
        if (device && !device.listeners("error").includes(handleTwilioError)) {
            logger.log("register device listeners");
            device.on(Device.EventName.Registered, handleDeviceRegistered);
            device.on(Device.EventName.Error, handleTwilioError);
            device.on(Device.EventName.Incoming, handleIncomingCall);
            device.on(Device.EventName.Registering, handleDeviceRegistering);
            device.on(Device.EventName.Unregistered, handleDeviceUnregistered);
            device.on(Device.EventName.Destroyed, handleDeviceDestroyed);
            device.on(Device.EventName.TokenWillExpire, handleTokenWillExpire);
            device.audio?.on("deviceChange", handleDeviceChange);
        }

        return () => {
            if (device && !device.isBusy) {
                logger.log("unregister device listeners");
                device.off(Device.EventName.Registered, handleDeviceRegistered);
                device.off(Device.EventName.Error, handleTwilioError);
                device.off(Device.EventName.Incoming, handleIncomingCall);
                device.off(Device.EventName.Registering, handleDeviceRegistering);
                device.off(Device.EventName.Unregistered, handleDeviceUnregistered);
                device.off(Device.EventName.Destroyed, handleDeviceDestroyed);
                device.off(Device.EventName.TokenWillExpire, handleTokenWillExpire);
                device.audio?.off("deviceChange", handleDeviceChange);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to watch the device.
    }, [device]);

    function handleDeviceRegistered() {
        if (!device) {
            return;
        }
        const { edge, state } = device;
        logger.log(`Device registered. Status: ${state} connected to edge: ${edge}`, {
            ...getAudioDeviceContext(undefined),
        });
        addLoggerGlobalContext("twilio.edge", String(edge));
    }

    function handleTwilioError(error: TwilioError) {
        if (twilioTokenErrorCodes.includes(error.code)) {
            logger.log("Twilio token error encountered. Reauthenticating Device", { error });
            // most times these errors point to the tab going into hibernation, so we kick it back up
            fetchNewAuthToken();
            return;
        }

        let errorMessage = twillioVoiceErrorCodeMappings[error.code] || "Unknown twilio error encountered";
        errorMessage += `. Error code: ${error.code}`;
        const errorKey = JSON.stringify(error);

        if (!twilioNoisyErrorsToIgnore.includes(error.code)) {
            renderErrorMessage({
                content: errorMessage,
                logger,
                error: error.twilioError?.originalError || new Error(errorMessage),
                extraData: { error },
            });
        }

        if ([EXPIRED_JWT, INVALID_JWT].includes(error.code)) {
            const deviceToken = device?.token;
            if (deviceToken) {
                logger.error(`Twilio voice device error ${error.code}`, {
                    deviceToken,
                });
            }
        }

        if (errorMessageCache[errorKey] && Date.now() - errorMessageCache[errorKey] < 10 * 1000) {
            return;
        }
        errorMessageCache[errorKey] = Date.now();
    }

    // this is where we capture any call that comes into our device
    function handleIncomingCall(call: Call): void {
        logger.log("Incoming event received");
        if (hasMicPerms$.getValue()) {
            bindCallEvents(call);
            call.accept();
        } else {
            logger.error("Rejecting incoming call due to lack of mic permissions");
            renderErrorMessage({ content: "Microphone has not been found.", logger });
            call.reject();
            clearActiveConferenceState();
            wrapCallTaskIfNeeded(call);
        }
    }

    function handleCallConnected(call: Call): void {
        const callStatus = call.status();
        logger.log("Call accepted with status:", {
            callStatus,
            taskSid: call.customParameters.get("taskSid"),
            originalTaskSid: call.customParameters.get("originalTaskSid"),
            progressiveDialObservabilityMeasure: call.customParameters.get("taskType") === "Progressive Dial",
            connectionCallSid: call.parameters.CallSid,
        });

        if (callStatus == Call.State.Closed) {
            dispatch(setOnActiveCall(false));
            setActiveCall();
            dispatch(clearConference());
            return;
        }

        const connectionCallSid = call.parameters.CallSid;
        if (connectionCallSid) {
            dispatch(setOnActiveCall(true));
            setActiveCall(call);

            const taskSid = call.customParameters.get("taskSid");
            dispatch(
                setDeviceParams({
                    taskSid,
                    ...omit(call.parameters, ["AccountSid"]),
                })
            );
        }
    }

    function handleDeviceDestroyed() {
        logger.log("Device destroyed");
        fetchNewAuthToken();
    }

    function handleTokenWillExpire() {
        logger.log("Device token will expire");
        fetchNewAuthToken();
    }

    function handleDeviceRegistering() {
        logger.log("Device registering");
    }

    function handleDeviceUnregistered() {
        logger.log("Device unregistered");
        fetchNewAuthToken();
    }

    function handleDeviceChange(lostActiveDevices: MediaDeviceInfo[]): void {
        logger.log("Device audio deviceChange", {
            ...getAudioDeviceContext(lostActiveDevices),
        });
    }

    function convertDeviceInfoMapToJson(deviceInfo: Map<string, MediaDeviceInfo>) {
        return Object.fromEntries(
            Array.from(deviceInfo.entries()).map(([key, value]) => [
                key,
                {
                    deviceId: value.deviceId,
                    label: value.label,
                    kind: value.kind,
                    groupId: value.groupId,
                },
            ])
        );
    }

    function convertDeviceInfoSetToJson(deviceInfo: TwilioOutputDevices | undefined) {
        return Array.from(deviceInfo?.get() || []).map((device) => ({
            deviceId: device.deviceId,
            label: device.label,
            kind: device.kind,
            groupId: device.groupId,
        }));
    }

    function getAudioDeviceContext(lostActiveDevices: MediaDeviceInfo[] | undefined) {
        return {
            audioDeviceContext: {
                availableInputDevices: convertDeviceInfoMapToJson(device?.audio?.availableInputDevices || new Map()),
                availableOutputDevices: convertDeviceInfoMapToJson(device?.audio?.availableOutputDevices || new Map()),
                activeInputDevice: device?.audio?.inputDevice,
                ringtoneDevices: convertDeviceInfoSetToJson(device?.audio?.ringtoneDevices),
                speakerDevices: convertDeviceInfoSetToJson(device?.audio?.speakerDevices),
                lostActiveDevices: lostActiveDevices,
            },
        };
    }

    function wrapCallTaskIfNeeded(call: Call) {
        // If an admin started the call by listening to an AI agent, but then decided to take over the call,
        // the call's taskSid customParameter will be for the original task and not the Takeover Call task
        // they're now creating.
        const taskSid = activeConferenceTaskRef.current?.taskSid || call.customParameters.get("taskSid");
        if (taskSid && (!managerActionRef?.current || managerActionRef.current === MANAGER_CALL_ACTIONS.takeOver)) {
            logger.log("Trying to wrap task after twilio device disconnected", { taskSid });
            // Tasks only automatically wrap when their associated conference ends.
            // Wrapping tasks manually ensures that an agent who leaves a conference with 2+ other participants can automatically wrap & disposition their task.
            wrapTask(taskSid);
        }
    }

    function handleCallDisconnected(call: Call) {
        logger.log(`Call disconnected with status:`, { callStatus: call.status() });
        wrapCallTaskIfNeeded(call);
        clearActiveConferenceState(call);
        logger.log("Device disconnect agent on active call status", { onActiveCall });
    }

    function clearActiveConferenceState(call?: Call) {
        dispatch(setOnActiveCall(false));
        setActiveCall();
        if (call?.customParameters.get("managerAction")) {
            dispatch(clearManagerAction());
        } else {
            logger.log("Clearing conference state", {
                call,
            });
            dispatch(clearConference());
        }

        dispatch(setDeviceParams({ taskSid: undefined }));
    }

    function bindCallEvents(call: Call) {
        const connectionDetails = {
            // Twilio stores the call sid in different places depending on the direction of the call
            callSid: call.parameters.CallSid ?? call.outboundConnectionId,
            taskSid: call.customParameters.get("taskSid"),
        };

        call.on("accept", handleCallConnected);
        call.on("accept", () => {
            // For some reason, certain calls drop this field
            connectionDetails.callSid = call.parameters.CallSid;
            callStateLogger.log("Starting call", { call: connectionDetails });
        });

        call.on("warning", (name, data) => {
            callStateLogger.warn(`Received warning: '${name}'`, {
                name,
                ...data,
                totalConnStats,
                recentStats: connStats.slice(-5),
            });
            dispatch(networkQualityWarning(name));
        });

        call.on("warning-cleared", (name) => {
            dispatch(resolveNetworkQualityWarning(name));
        });

        call.on("cancel", () => {
            callStateLogger.log("Call cancelled", { call: connectionDetails });
            clearActiveConferenceState(call);
            dispatch(clearNetworkQualityWarnings());
        });

        call.on("error", (error) => {
            callStateLogger.warn("Call got error", { call: connectionDetails, error });
        });

        call.on("reconnecting", (error) => {
            callStateLogger.log("Call reconnecting from error", { call: connectionDetails, error });
        });

        call.on("reconnected", () => {
            callStateLogger.log("Call reconnected", { call: connectionDetails });
        });

        call.on("disconnect", handleCallDisconnected);
        call.on("disconnect", () => {
            callStateLogger.log("Call ended", { call: connectionDetails });
            dispatch(clearNetworkQualityWarnings());
        });

        call.on("sample", (sample) => {
            audioStatisticsLogger.debug(sample);
            addConnectionStat(sample);
            const taskSid = call.customParameters.get("taskSid");
            if (taskSid) {
                dispatch(updateConnectionQualityStatistics(taskSid, sample));
            }
        });

        function addConnectionStat({ totals, timestamp, ...stats }: ConnectionStats): void {
            const expiredSampleTime = Math.floor(timestamp) - 60 * 1000; // Expire samples more than 60 seconds old

            setTotalConnStats(totals);

            (stats as ConnectionStats).timestamp = timestamp;
            setConnStats((prevConnStats) => {
                return prevConnStats
                    .filter(({ timestamp }) => timestamp < expiredSampleTime)
                    .concat(stats as ConnectionStats);
            });
        }
    }
    return {
        handleCallConnected,
        handleIncomingCall,
        handleCallDisconnected,
    };
}
